import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function Article({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          GIS and Land Surveying Resources
        </h1>
        <ArticleAuthor />
        <p>
          Geographic information systems (GIS) use multiple data sources to
          create detailed informational models about an area based on a specific
          location or point on a map. These areas can be very small or as large
          as an ocean. All maps display locations, but GIS adds other data like
          the names of residents or population density on streets to create a
          more thorough understanding of an area.
        </p>
        <h2 className={styles["Article__sub-heading"]}>What Is a GIS?</h2>
        <p>
          Have you ever entered the name of a restaurant or landmark into a map
          app on a smartphone? If so, you've used a GIS system. These systems
          overlay information like restaurant names onto maps. It allows users
          to not only get detailed, turn-by-turn directions to a location but
          also type in things like "Indian restaurant" to receive a list of all
          nearby Indian restaurants. GIS also allows maps to account for current
          traffic conditions to help users find the quickest, most efficient way
          to travel. Currently, the Environmental Systems Research Institute, or
          ESRI, is the leading organization behind GIS.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://education.nationalgeographic.org/resource/geographic-information-system-gis/">
                GIS (Geographic Information System)
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.usgs.gov/faqs/what-geographic-information-system-gis">
                What is a Geographic Information System (GIS)?
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://researchguides.library.wisc.edu/GIS">
                Mapping and Geographic Information Systems (GIS): What is GIS?
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://onlinedegrees.kent.edu/geography/geographic-information-science/community/what-is-gis-data-and-how-is-it-used">
                What Is GIS Data, and How Is It Used?
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://carrollengineering.com/what-is-gis-and-how-can-it-benefit-my-project/">
                What Is GIS and How Can It Benefit My Project?
              </a>
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>History of GIS</h2>
        <p>
          Mapmaking, also known as cartography, is a very old art and science
          for humankind. The Ancient Greeks made some of the oldest, still
          surviving maps. Maps were vital in the growth of trade routes,
          especially when sailors took to the high seas. However, for most of
          history, maps were relatively imprecise. They relied on people's
          memories and used primitive devices for their measurements. Satellites
          and aerial cameras have forever changed how people make maps and how
          accurate and precise those maps are. Since governments have always
          been one of the largest producers and consumers of maps, it makes
          sense that governments led the way in GIS development. Canada launched
          the first GIS back in the 1960s. Back then, the systems used punch
          cards read by a very large analog computer. As computers advanced
          technologically, so did GIS, until eventually systems were in place
          that allowed them to be used by the general public.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://gisgeography.com/history-of-gis/">
                The Remarkable History of GIS
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.bcs.org/articles-opinion-and-research/the-history-of-geographic-information-systems-gis/">
                The History of Geographic Information Systems (GIS)
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://vermont.pbslearningmedia.org/resource/psu10sci.vid.geospatial.histgis/geospatial-revolution-history-of-gis/">
                History of GIS | Geospatial Revolution
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.smithsonianmag.com/history/unlikely-history-origins-modern-maps-180951617/">
                The Unlikely History of the Origins of Modern Maps
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://complexsystemstheory.net/2020/04/21/the-evolution-of-gis/">
                The Evolution of GIS
              </a>
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>How Does a GIS Work?</h2>
        <p>
          GIS needs two things to function. One is map data, and the other is
          the user's location. No matter how complicated or advanced any GIS is,
          these two things are the heart of its functioning. So yes, NASA GIS
          and the GIS that powers Apple Maps work in basically the same way.
          Once the location parameters have been established, the system
          overlays relevant data in layers. Think about a city block. The layers
          will include road data, street numbers, nearby railroad crossings, and
          information about all businesses on that block.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.elmhurst.edu/blog/gis-mapping-tools/">
                GIS Mapping Tools and How They Work
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://mangomap.com/gis-mapping">GIS Mapping</a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://study.com/academy/lesson/geographic-information-system-gis-using-maps-locations-to-make-decisions.html">
                Geographic Information System (GIS): Using Maps &amp; Locations
                to Make Decisions
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.gislounge.com/gis-software-components-examples-using-openmap-and-mapobjects/">
                GIS Software Components: Examples Using OpenMap and MapObjects
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.hec.usace.army.mil/confluence/hmsdocs/hmsguides/gis-tutorials-and-guides/creating-new-basin-model-elements-by-importing-gis-features-in-shapefiles">
                Creating New Basin Model Elements by Importing GIS Features in
                Shapefiles
              </a>
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Uses of a GIS</h2>
        <p>
          There are endless uses for GIS. Everyone from tourists needing walking
          directions to a nearby museum to climate change experts studying how
          weather patterns impact certain terrains uses GIS. Think about weather
          maps distributed by the local news and smartphone weather apps. Both
          are possible thanks to GIS. Another common use that most people see is
          the electoral maps on the internet and the news during Election
          seasons. Both pre-election polling and predictions and maps showing
          results are possible with GIS.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.gislounge.com/basic-uses-of-gis/">
                Basic Uses of GIS
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.geoowl.com/other/gis-in-everyday-life/">
                GIS in Everyday Life
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.cdc.gov/gis/index.htm">
                GIS and Public Health at CDC
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://smallfarms.cornell.edu/2017/04/use-of-gis/">
                Use of GIS in Agriculture
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.epa.gov/frs/gis-applications">
                GIS Applications
              </a>
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Glossary of Terms</h2>
        <p>
          GIS, like all fields, has specific phrases and terms used to describe
          its various functions and practices. ArcGIS, for example, is a variety
          of programs that work together to use GIS and make new types of maps
          and information modeling possible. Topography is an older term that's
          been around since maps were hand-drawn that describes the study of a
          planet's terrain. Cartography is another word for mapmaking. Geocoding
          is when specific points are added to a map based on a location's
          street address.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://health.mo.gov/data/gis/pdf/GISGlossary.pdf">
                GIS Glossary of Terms
              </a>
              &nbsp;(PDF)
            </p>
          </li>
          <li>
            <p>
              <a href="https://developers.arcgis.com/documentation/glossary/">
                ArcGIS Developer Glossary
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.cockroachlabs.com/docs/stable/spatial-glossary.html">
                Spatial and GIS Glossary of Terms
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.mncppcapps.org/planning/AjaxPlanningGlossary/default.cfm?letter=M">
                Glossary of Planning and Geographic Information Systems (GIS)
                Terms
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.geofields.com/resources/oil-gas-pipeline-gis-glossary-dictionary/">
                Oil &amp; Gas Pipeline GIS Glossary / Dictionary
              </a>
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Careers in GIS</h2>
        <p>
          GIS is a rapidly growing field with rising demand across various
          industries. For example, all industries and organizations impacted by
          climate change use GIS to understand how it will impact their business
          or organization. City planners use GIS to visualize how people move
          through a city. Even sewage engineers use GIS when designing or
          upgrading systems. Everyday demand for more sophisticated and yet
          easy-to-use GIS interfaces grows the need for designers to create
          these apps and interfaces grows with it. Governments and other
          organizations need people who can analyze data and use GIS to develop
          models. Land surveyors also use GIS and are in hot demand.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://gis.usc.edu/blog/4-reasons-to-consider-gis-as-a-career/">
                4 Reasons GIS Careers May be the Best Careers
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://unity.edu/careers/gis-analyst/">
                What is a GIS Analyst?
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.indeed.com/career-advice/finding-a-job/types-of-jobs-with-degree-in-gis">
                26 Types of Jobs You Can Get With a Degree in GIS
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://bootcampgis.com/jobs-that-use-gis-youll-be-surprised-at-the-variety/">
                Jobs That Use GIS. You'll Be Surprised At The Variety
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.careers360.com/careers/gis-expert">
                Who is a GIS Expert?
              </a>
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Land Surveying Resources
        </h2>
        <ul>
          <li>
            <p>
              <a href="https://landsurveyorsunited.com/">Land Surveyors</a>: GIS
              allows land surveyors to create more accurate surveys for their
              clients.
            </p>
          </li>
          <li>
            <p>
              <a href="https://altalandsurvey.com/surveying-resources/">
                Resources for Land Owners, Legal and Real Estate Professionals
              </a>
              : Each state has laws regarding land surveying.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.softdig.com/blog/land-surveying-gis-partnership/">
                How Land Surveying and GIS Work Together
              </a>
              : GIS is a tool to help surveyors work more efficiently.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.esri.com/content/dam/esrisites/sitecore-archive/Files/Pdfs/library/brochures/pdfs/gis-sols-for-surveying.pdf">
                GIS Solutions for Surveying
              </a>
              &nbsp;(PDF): Surveyors can use GIS to conduct spatial analysis,
              overlay data over topographical maps, and easily add data and
              information from other programs.
            </p>
          </li>
          <li>
            <p>
              <a href="https://info.courthousedirect.com/blog/how-gis-tools-are-changing-the-practice-of-land-surveying">
                How GIS Tools Are Changing the Practice of Land Surveying
              </a>
              : GIS can change a surveyor's daily workflow and make them
              productive.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.nsgic.org/licensing">
                GIS &amp; Land Surveyors Requirements for Licensing
              </a>
              : In 2006, the National Council of Examiners for Engineering and
              Surveying (NCEES) released Model Rules to describe the roles and
              responsibilities of surveyors using GIS.
            </p>
          </li>
          <li>
            <p>
              <a href="https://id.land/blog/the-evolution-and-application-of-gis-in-surveying">
                The Evolution And Application Of GIS In Surveying
              </a>
              : The earliest land surveyors practiced their trade around 1400
              B.C.E. Since then, how they practice their craft has changed with
              technological advances.
            </p>
          </li>
          <li>
            <p>
              <a href="https://geoinfotech.ng/differences-between-gis-and-surveying/">
                Differences Between GIS and Surveying
              </a>
              : Surveying focuses on the accuracy of things like{" "}
              <a href="https://www.realestateagents.com/real-estate-agents/spokane-wa">
                property
              </a>{" "}
              lines, while GIS focuses more on spatial analysis and data
              modeling.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.asprs.org/a/news/ncees/Joffe_URISA_Atlanta_10-15-03_short.pdf">
                Implementing the Surveyors' and GIS Professionals' Task Force
                Recommendations
              </a>
              : Each state creates its own laws and regulations regarding
              surveyors.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.kentwa.gov/departments/public-works/land-survey-geographic-information-systems-gis">
                Land Survey/Geographic Information Systems (GIS)
              </a>
              :{" "}
              <a href="https://www.realestateagents.com/real-estate-agents/kent-wa">
                Kent, Washington
              </a>
              , makes public GIS available to surveyors.
            </p>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={"GIS and Land Surveying Resources | RealEstateAgents.com"}
        description={
          "Discover the basics of Geographic Information Systems and resources for Land surveying. GIS info includes a history, what it is, how it works, and more."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/gis-and-land-surveying-resources",
          },
          headline: "GIS and Land Surveying Resources",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
